import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Printer, Copy, PaperPlaneTilt, GraduationCap, Sparkle, ArrowsClockwise, Archive, Envelope,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PageHeader } from '@/components/enterprise/PageHeader';
import { EmptyState } from '@/components/enterprise/EmptyState';
import { devApi } from './devApi';
import { jstDateTime } from '@/utils/jstDate';

const yen = (v) => `¥${Math.round(v || 0).toLocaleString('ja-JP')}`;

const monthOptions = () => {
  const out = [];
  const d = new Date();
  for (let i = 0; i < 18; i += 1) {
    const m = new Date(d.getFullYear(), d.getMonth() - i, 1);
    const key = `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, '0')}`;
    out.push([key, `${m.getFullYear()}年${m.getMonth() + 1}月`]);
  }
  return out;
};

const Section = ({ no, title, hint, children, testid }) => (
  <section className="mt-5 break-inside-avoid" data-testid={testid}>
    <h2 className="border-b border-zinc-300 pb-1 text-sm font-bold text-zinc-900">{no}. {title}</h2>
    {hint && <p className="mt-1 text-xs text-zinc-600">{hint}</p>}
    <div className="mt-2">{children}</div>
  </section>
);

const Rank = ({ rows, render, testid, empty = '記録はありません。' }) => (
  <ol className="space-y-1.5" data-testid={testid}>
    {rows.length === 0 && <p className="text-sm text-zinc-500">{empty}</p>}
    {rows.map((r, i) => (
      <li key={r.label || r.development_id || r.tested_item || i}
        className="flex flex-wrap items-baseline gap-x-2 border-b border-zinc-100 pb-1 text-sm">
        <span className="w-5 shrink-0 text-xs font-bold text-zinc-500">{i + 1}</span>
        {render(r)}
      </li>
    ))}
  </ol>
);

/** メール配信の設定 — 管理者・マネージャーに加えて任意の宛先を追加できる。 */
const DigestDelivery = () => {
  const [c, setC] = useState(null);
  const [mail, setMail] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { devApi.digestConfig().then(setC).catch(() => {}); }, []);

  const save = async (next) => {
    setBusy(true);
    try {
      setC(await devApi.saveDigestConfig(next));
      setMail('');
      toast.success('メール配信の設定を保存しました');
    } catch (e) { toast.error(e.response?.data?.detail || '保存できませんでした'); }
    finally { setBusy(false); }
  };

  if (!c) return null;
  return (
    <div className="mx-auto max-w-5xl rounded-lg border border-zinc-300 bg-white p-4 shadow-sm print:hidden"
      data-testid="dev-learning-delivery">
      <p className="text-sm font-bold text-zinc-900">
        <Envelope size={14} className="mr-1 inline text-teal-700" /> メール配信
      </p>
      <p className="text-xs text-zinc-600">
        管理者・マネージャーには自動で送られます。社外・共有アドレスを追加したい場合はここに登録してください。
      </p>
      <label className="mt-2 flex items-center gap-2 text-sm text-zinc-800">
        <input type="checkbox" checked={c.email_enabled} disabled={busy}
          onChange={(e) => save({ ...c, email_enabled: e.target.checked })}
          data-testid="dev-learning-email-toggle" />
        メールでも配信する
      </label>
      <div className="mt-2 flex flex-wrap gap-1">
        {c.extra_emails.map((e) => (
          <span key={e} className="flex items-center gap-1 rounded-full border border-zinc-300 px-2 py-0.5 text-xs text-zinc-800"
            data-testid={`dev-learning-email-${e}`}>
            {e}
            <button type="button" disabled={busy}
              onClick={() => save({ ...c, extra_emails: c.extra_emails.filter((x) => x !== e) })}
              className="font-bold" data-testid={`dev-learning-email-remove-${e}`}>×</button>
          </span>
        ))}
        {c.extra_emails.length === 0 && <span className="text-xs text-zinc-500">追加の宛先はありません。</span>}
      </div>
      <div className="mt-2 flex gap-2">
        <Input value={mail} onChange={(e) => setMail(e.target.value)} placeholder="name@example.com"
          className="h-8 w-64 text-sm" data-testid="dev-learning-email-input" />
        <Button variant="outline" size="sm" disabled={busy || !mail.includes('@')}
          onClick={() => save({ ...c, extra_emails: [...c.extra_emails, mail.trim()] })}
          data-testid="dev-learning-email-add">宛先を追加</Button>
      </div>
    </div>
  );
};

/** 学びのダイジェスト — 何が繰り返し失敗し、何にお金がかかり、何を直すべきか。 */
export default function LearningDigest() {
  const nav = useNavigate();
  const [month, setMonth] = useState(monthOptions()[0][0]);
  const [d, setD] = useState(null);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setD(null);
    devApi.learningDigest(month).then(setD)
      .catch(() => toast.error('学びのダイジェストを読み込めませんでした'));
  }, [month]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { devApi.learningDigestLog().then((r) => setLog(r.rows || [])).catch(() => {}); }, []);

  const send = async () => {
    setBusy(true);
    try {
      const r = await devApi.sendLearningDigest(month);
      toast.success(`${r.sent}名に送信しました`);
      const l = await devApi.learningDigestLog();
      setLog(l.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || '送信できませんでした');
    } finally { setBusy(false); }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(d.text);
      toast.success('本文をコピーしました');
    } catch { toast.error('コピーできませんでした'); }
  };

  if (!d) {
    return <div className="p-6 text-sm text-zinc-600" data-testid="dev-learning-loading">読み込み中…</div>;
  }
  const t = d.totals;

  return (
    <div className="space-y-4 print:space-y-2" data-testid="dev-learning-page">
      <div className="print:hidden">
        <PageHeader title="学びのダイジェスト"
          description="開発から何を学んだか — 繰り返し失敗していること、お金がかかっていること、次に直すこと。"
          testid="dev-learning-header"
          primary={
            <Button onClick={send} disabled={busy} data-testid="dev-learning-send-btn">
              <PaperPlaneTilt size={15} className="mr-1" /> 送信
            </Button>
          }
          menuItems={[
            { label: '再生成', icon: <ArrowsClockwise size={14} />, onClick: load,
              testid: 'dev-learning-reload-btn' },
            { label: '本文をコピー', icon: <Copy size={14} />, onClick: copy,
              testid: 'dev-learning-copy-btn' },
            { label: '印刷 / PDF', icon: <Printer size={14} />, onClick: () => window.print(),
              testid: 'dev-learning-print-btn' },
            { label: '原価分析を開く', icon: <GraduationCap size={14} />,
              onClick: () => nav('/engineering/developments/cost'),
              testid: 'dev-learning-cost-btn' },
            { label: '全体傾向を開く', icon: <GraduationCap size={14} />,
              onClick: () => nav('/engineering/developments/analytics'),
              testid: 'dev-learning-analytics-btn' },
          ]}
          secondary={
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="w-40" data-testid="dev-learning-month">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-72 bg-white">
                {monthOptions().map(([v, l]) => (
                  <SelectItem key={v} value={v}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          } />
      </div>

      <div className="mx-auto max-w-5xl rounded-lg border border-zinc-300 bg-white p-5 shadow-sm print:border-0 print:p-0 print:shadow-none"
        data-testid="dev-learning-sheet">
        <header className="border-b-2 border-zinc-800 pb-2">
          <p className="text-xs font-semibold text-zinc-600">開発案件管理 — 学びのダイジェスト</p>
          <h1 className="text-2xl font-bold text-zinc-900">{d.month_label}</h1>
          <p className="mt-1 text-xs text-zinc-600">
            作成 {jstDateTime(d.generated_at)} ／ 事実はデータから集計、コメントのみAI
          </p>
        </header>

        <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-5">
          {[['今月の不具合', t.failures_in_month, 'text-rose-700', 'failures'],
            ['今月の再発', t.recurrences_in_month, 'text-rose-700', 'recurrences'],
            ['改訂の累計', t.revisions_total, 'text-amber-700', 'revisions'],
            ['スクラップ費（累計）', yen(t.scrap_cost), 'text-amber-700', 'scrap'],
            ['開発原価の累計', yen(t.final_cost), 'text-zinc-900', 'cost']].map(([l, v, tone, k]) => (
            <div key={k} className="rounded-md border border-zinc-300 px-3 py-2"
              data-testid={`dev-learning-tile-${k}`}>
              <p className="text-xs font-semibold text-zinc-600">{l}</p>
              <p className={`text-xl font-bold tabular-nums ${tone}`}>{v}</p>
            </div>
          ))}
        </div>

        {d.ai_commentary?.length > 0 ? (
          <div className="mt-4 rounded-md border border-teal-300 bg-teal-50/60 px-3 py-2"
            data-testid="dev-learning-ai">
            <p className="text-xs font-bold text-teal-900">
              <Sparkle size={12} className="mr-1 inline" /> AIコメント（事実の解釈）
            </p>
            <p className="whitespace-pre-wrap text-sm text-zinc-800">{d.ai_commentary[0].text}</p>
          </div>
        ) : (
          <p className="mt-4 text-xs text-zinc-500" data-testid="dev-learning-ai-unavailable">
            AIコメントは利用できません。以下の集計はすべてデータから直接算出しています。
          </p>
        )}

        {t.failures_total === 0 && t.revisions_total === 0 && t.scrap_events === 0 ? (
          <EmptyState icon={GraduationCap} title="まだ学べる記録がありません"
            description="検証・改訂・スクラップの記録が増えると、ここに傾向と推奨が表示されます。"
            testid="dev-learning-empty" />
        ) : (
          <>
            <Section no="1" title="最も落ちている部品" hint="検証で不具合になった回数の多い順"
              testid="dev-learning-parts">
              <Rank rows={d.most_failed_parts} testid="dev-learning-list-parts"
                render={(r) => (
                  <>
                    <span className="font-semibold text-zinc-900">{r.label}</span>
                    <span className="text-rose-700">不具合 {r.count}件</span>
                    <span className="text-xs text-zinc-600">
                      {r.projects.length}案件（{r.projects.join('、')}）
                      {r.categories?.length ? ` · ${r.categories.join('、')}` : ''}
                    </span>
                  </>
                )} />
            </Section>

            <Section no="2" title="最も手戻りしている図面" hint="不具合と改訂の合計が多い順"
              testid="dev-learning-drawings">
              <Rank rows={d.most_failed_drawings} testid="dev-learning-list-drawings"
                render={(r) => (
                  <>
                    <span className="font-semibold text-zinc-900">{r.label}</span>
                    <span className="text-rose-700">不具合 {r.count}件</span>
                    <span className="text-amber-800">改訂 {r.revisions}回</span>
                    <span className="text-xs text-zinc-600">{(r.parts || []).join('、')}</span>
                    <span className="text-xs text-teal-900">→ 調達前に設計レビューを行う</span>
                  </>
                )} />
            </Section>

            <Section no="3" title="多い不具合の分類と次の一手" hint="分類ごとの件数と推奨する対策"
              testid="dev-learning-categories">
              <Rank rows={d.validation_failures} testid="dev-learning-list-categories"
                render={(r) => (
                  <>
                    <span className="font-semibold text-zinc-900">{r.label}</span>
                    <span className="text-zinc-900">{r.count}件</span>
                    {r.recurrences > 0 && <span className="text-rose-700">再発 {r.recurrences}件</span>}
                    <span className="text-xs text-teal-900">→ {r.advice}</span>
                  </>
                )} />
            </Section>

            <Section no="4" title="スクラップの理由" hint="部品を廃棄した理由と数量"
              testid="dev-learning-scrap">
              <Rank rows={d.scrap_reasons} testid="dev-learning-list-scrap"
                render={(r) => (
                  <>
                    <span className="font-semibold text-zinc-900">{r.label}</span>
                    <span className="text-amber-800">{r.count}件 / 数量 {r.qty}</span>
                    <span className="text-xs text-zinc-600">{(r.parts || []).join('、')}</span>
                  </>
                )} />
            </Section>

            <Section no="5" title="手戻りが多い案件" hint="改訂と不具合の合計が多い順"
              testid="dev-learning-rework">
              <Rank rows={d.highest_rework} testid="dev-learning-list-rework"
                render={(r) => (
                  <button type="button" className="text-left"
                    onClick={() => nav(`/engineering/developments/${r.development_id}`)}
                    data-testid={`dev-learning-rework-${r.development_id}`}>
                    <span className="font-semibold text-zinc-900 underline">{r.label}</span>
                    <span className="ml-2 text-amber-800">改訂 {r.revisions}回</span>
                    <span className="ml-2 text-rose-700">不具合 {r.failures}件</span>
                    <span className="ml-2 text-xs text-zinc-600">{r.stage}</span>
                  </button>
                )} />
            </Section>

            <Section no="6" title="原価が大きい案件 / 想定超過" hint="最終原価と、想定コストとの差"
              testid="dev-learning-cost">
              <Rank rows={d.highest_cost} testid="dev-learning-list-cost"
                render={(r) => (
                  <button type="button" className="text-left"
                    onClick={() => nav(`/engineering/developments/${r.development_id}`)}
                    data-testid={`dev-learning-cost-${r.development_id}`}>
                    <span className="font-semibold text-zinc-900 underline">{r.label}</span>
                    <span className="ml-2 tabular-nums">{yen(r.final_cost)}</span>
                    <span className="ml-2 text-xs text-zinc-600">材料 {yen(r.material_cost)}</span>
                    {r.scrap_cost > 0 && (
                      <span className="ml-2 text-xs text-amber-800">スクラップ {yen(r.scrap_cost)}</span>
                    )}
                  </button>
                )} />
              <p className="mt-3 text-xs font-semibold text-zinc-700">想定より費用がかかった案件</p>
              <Rank rows={d.cost_overruns} testid="dev-learning-list-overrun"
                empty="想定コストが記録された案件がまだありません（構想の「想定コスト」に金額を入れると比較できます）。"
                render={(r) => (
                  <button type="button" className="text-left"
                    onClick={() => nav(`/engineering/developments/${r.development_id}`)}
                    data-testid={`dev-learning-overrun-${r.development_id}`}>
                    <span className="font-semibold text-zinc-900 underline">{r.label}</span>
                    <span className="ml-2 text-xs text-zinc-600">
                      想定 {yen(r.estimated_cost)} → 実績 {yen(r.actual_cost)}
                    </span>
                    <span className="ml-2 font-bold text-rose-700">
                      +{yen(r.variance)}{r.variance_pct !== null ? `（+${r.variance_pct}%）` : ''}
                    </span>
                  </button>
                )} />
            </Section>

            <Section no="7" title="うまくいった進め方" hint="不具合と改訂が少なく完了した案件 — 真似する価値があります"
              testid="dev-learning-patterns">
              <Rank rows={d.successful_patterns} testid="dev-learning-list-patterns"
                empty="完了した案件がまだありません。"
                render={(r) => (
                  <button type="button" className="text-left"
                    onClick={() => nav(`/engineering/developments/${r.development_id}`)}
                    data-testid={`dev-learning-pattern-${r.development_id}`}>
                    <span className="font-semibold text-zinc-900 underline">{r.label}</span>
                    <span className="ml-2 text-xs text-zinc-600">{r.type_label}</span>
                    <span className="ml-2 text-emerald-700">不具合 {r.failures}件</span>
                    <span className="ml-2 text-emerald-700">改訂 {r.revisions}回</span>
                    <span className="ml-2 text-xs tabular-nums text-zinc-700">{yen(r.final_cost)}</span>
                  </button>
                )} />
            </Section>
          </>
        )}
      </div>

      <DigestDelivery />

      {log.length > 0 && (        <div className="mx-auto max-w-5xl rounded-lg border border-zinc-300 bg-white p-4 shadow-sm print:hidden"
          data-testid="dev-learning-archive">
          <p className="text-sm font-bold text-zinc-900">
            <Archive size={14} className="mr-1 inline text-zinc-600" /> 配信の保管庫
          </p>
          <ul className="mt-2 divide-y divide-zinc-200">
            {log.map((r) => (
              <li key={r.id} className="py-2 text-sm" data-testid={`dev-learning-archive-${r.id}`}>
                <span className="font-semibold text-zinc-900">{r.month_label}</span>
                <span className="ml-2 text-xs text-zinc-600">
                  {jstDateTime(r.sent_at)} · {r.recipients}名
                  {r.auto ? ' · 自動配信' : ` · ${r.sent_by_name || '手動'}`}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
